/*
 * CLARTEXT · Gefahrstoffmodul · Phase C · Kombiniertes Sichtungs-Modal.
 *
 * Betreiberentscheidung 24.08.2026: EIN einziges Modal statt zwei
 * getrennter Dialoge (früher PreAnalyzeDialog-hazmat-Zweig +
 * HazmatSdbSightingModal). Von Anfang an gleichzeitig sichtbar:
 *   1. Produkt-Name + drei Pflichtangaben (Menge · Häufigkeit · Substitution)
 *   2. Drag-&-Drop-Feld — falls der Anwender das SDB bereits hat
 *   3. Automatische Tavily-Suche im Hintergrund (startet 500ms nach
 *      Modal-Öffnen bei vorbefülltem Produktnamen; bei Änderung wird
 *      ein bereits laufender Job stillgelegt und ein neuer gestartet)
 *
 * Race-Regel: User-Upload gewinnt immer. Wenn Auto-Fund und Upload
 * gleichzeitig fertig werden, wird das Auto-Ergebnis verworfen.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ShieldCheck, ShieldAlert, Loader2, ExternalLink, Check, X, Upload,
  FileText, ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";

const POLL_INTERVAL_MS = 2000;
const MAX_POLLS = 60;                 // 60 × 2s = 120s Sicherheitslimit
const PRODUCT_NAME_DEBOUNCE_MS = 500; // Trigger-Modell: 500ms nach letzter Eingabe

const FREQUENCY_TIERS = [
  { code: "selten", label: "Selten (wenige Male im Jahr)" },
  { code: "gelegentlich", label: "Gelegentlich (mehrmals im Monat)" },
  { code: "regelmaessig", label: "Regelmäßig (wöchentlich)" },
  { code: "taeglich", label: "Täglich (mehrmals täglich)" },
];

const SUBSTITUTION_REASONS = [
  { code: "no_alternative_available",
    label: "Kein ungefährlicherer, gleich geeigneter Ersatzstoff verfügbar" },
  { code: "alternative_not_viable",
    label: "Ersatzstoff wirtschaftlich/technisch nicht vertretbar" },
  { code: "already_safest_option",
    label: "Bereits sicherste verfügbare Option am Markt" },
];

const DISPLAY_SECTIONS = [
  { key: "abschnitt_1_produkt", title: "1 · Produkt & Hersteller",
    fields: [["produktname", "Produktname"], ["hersteller", "Hersteller"],
             ["artikelnummer", "Artikelnummer"], ["verwendungszweck", "Verwendungszweck"]] },
  { key: "abschnitt_2_gefahren", title: "2 · Gefahren",
    fields: [["signalwort", "Signalwort"], ["h_saetze", "H-Sätze"], ["p_saetze", "P-Sätze"]] },
];

const _val = (section, key) => {
  const v = section?.[key];
  if (v === null || v === undefined) return "";
  return String(v).trim();
};

/**
 * @param {object} props
 * @param {boolean} props.open
 * @param {object|null} props.initial  { topic, quantityQuestion, seedModel? }
 * @param {function} props.onConfirm   (declaration, prefetchIdOrNull) => void
 * @param {function} props.onCancel    () => void
 * @param {function} props.onFleeHazmat  () => void — Rückgriff auf normalen Welt-Weg
 *
 * Betreiberentscheidung 30.08.2026: Der frühere „Ohne SDB fortfahren"-Pfad
 * (onSkipSdb) ist entfernt — er erzeugte praktisch nutzlose Registry-
 * „Waisen" (alle Klassifikations-/PSA-Felder leer). Eine KI-gestützte
 * „Ohne SDB erzeugen"-Lösung ist bewusst zurückgestellt. Verbleibende
 * Wege: SDB finden/hochladen ODER „Kein Gefahrstoff — normale Welt".
 */
export const HazmatSdbSightingModal = ({
  open, initial, onConfirm, onCancel, onFleeHazmat,
}) => {
  // --- Pflichtangaben ------------------------------------------------------
  const [productName, setProductName] = useState("");
  const [quantityTierIndex, setQuantityTierIndex] = useState(null); // 0|1|2|"custom"|null
  const [quantityCustomAmount, setQuantityCustomAmount] = useState("");
  const [quantityCustomUnit, setQuantityCustomUnit] = useState("");
  const [frequencyCode, setFrequencyCode] = useState(null);
  // Betreiberentscheidung 30.08.2026: Die Substitutions-Checkbox ist
  // entfernt — es gibt nur noch den Begründungs-Pfad (checked bleibt als
  // Datenfeld fest false; Pflicht: Option ODER Freitext, wie bisher).
  const [substitutionChecked, setSubstitutionChecked] = useState(false);
  const [substitutionReasonCode, setSubstitutionReasonCode] = useState("");
  const [substitutionFreeText, setSubstitutionFreeText] = useState("");

  // --- SDB-Suche (Auto-Fund) -----------------------------------------------
  const [autoJobId, setAutoJobId] = useState(null);
  const [autoStatus, setAutoStatus] = useState("idle");
    // idle | pending | searching | extracting | done | error | cancelled
  const [autoResult, setAutoResult] = useState(null);
  const [autoProgress, setAutoProgress] = useState("");
  const [autoError, setAutoError] = useState("");

  // --- Manueller Upload ----------------------------------------------------
  const [uploadStatus, setUploadStatus] = useState("idle");
    // idle | uploading | done | error
  const [uploadResult, setUploadResult] = useState(null);
  const [uploadError, setUploadError] = useState("");
  const [dragActive, setDragActive] = useState(false);

  // --- Refs für async-Sicherheit ------------------------------------------
  const activeJobIdRef = useRef(null);
  const timerRef = useRef(null);
  const debounceRef = useRef(null);
  const pollCountRef = useRef(0);
  const uploadWonRef = useRef(false);      // User-Upload gewinnt immer
  const startedNameRef = useRef("");        // Job wurde mit welchem Namen gestartet
  const cancelledRef = useRef(false);       // Modal-Reset-Flag
  const initialRef = useRef(null);          // wechselte initial, obwohl Modal offen blieb?
  // Q++ Schritt 3 (24.08.2026): Härtung gegen Doppel-Trigger von
  // `onConfirm` — falls `hasSdbExtract` kurzzeitig flackert oder der
  // useEffect zwei Renderings hinter dem Extract-Ende auslöst, verhindert
  // dieser Ref-Guard einen zweiten `onConfirm`-Aufruf.
  const generateTriggeredRef = useRef(false);

  // --- Q++ (24.08.2026, Schritt 3 "frühere Button-Freigabe") ---------------
  //
  // `awaitingExtract` markiert den kurzen Übergang, in dem der User bereits
  // auf "Welt erstellen" geklickt hat, während der Backend-Job noch im
  // `extracting`-Zustand steckt. In diesem Fall zeigt das Modal einen
  // Warte-Spinner, das bestehende Polling läuft weiter, und sobald das
  // finale `archived_preview` eintrifft, wird `handleConfirmWithSdb` einmal
  // automatisch nachgezogen. Backend bleibt strikt beim vollständigen
  // Prefetch — kein Blocking, kein Doppelaufruf von `generate`.
  const [awaitingExtract, setAwaitingExtract] = useState(false);

  // --- Reset beim Öffnen/Schließen ----------------------------------------
  //
  // Fix 24.08.2026 (Frontend-State-Race): Reset muss auch bei einem
  // `initial`-Wechsel mit weiterhin offenem Modal greifen. Ohne diesen
  // Zweig hing eine erneute Preanalyse (die dasselbe Modal mit neuem
  // Topic re-öffnete) im alten State-Referenzen-Kokon fest — Polling
  // ging weiter auf den alten Job, autoStatus blieb "done", der
  // Debounce-Guard verhinderte den Neustart.
  useEffect(() => {
    const initialChanged = open && initialRef.current !== initial && initialRef.current !== null;
    const wasClosed = !open;

    if (wasClosed || initialChanged) {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      setQuantityTierIndex(null);
      setQuantityCustomAmount("");
      setQuantityCustomUnit("");
      setFrequencyCode(null);
      setSubstitutionChecked(false);
      setSubstitutionReasonCode("");
      setSubstitutionFreeText("");
      setAutoJobId(null);
      setAutoStatus("idle");
      setAutoResult(null);
      setAutoProgress("");
      setAutoError("");
      setUploadStatus("idle");
      setUploadResult(null);
      setUploadError("");
      setDragActive(false);
      setAwaitingExtract(false);
      generateTriggeredRef.current = false;
      activeJobIdRef.current = null;
      startedNameRef.current = "";
      uploadWonRef.current = false;
      pollCountRef.current = 0;
      if (wasClosed) {
        setProductName("");
        initialRef.current = null;
        return;
      }
      // initialChanged: Modal bleibt offen, Reset ist gerade eben passiert,
      // wir fallen in den Prefill-Zweig weiter unten durch.
    }

    cancelledRef.current = false;
    initialRef.current = initial;
    // Produktname aus dem Suchbegriff vorbelegen (falls plausibel und
    // vorhanden — nur bis 200 Zeichen, sonst zurücksetzen).
    const seed = String(initial?.seedModel || initial?.topic || "").trim();
    if (seed && seed.length <= 200) {
      setProductName(seed);
    } else {
      setProductName("");
    }
  }, [open, initial]);

  const _startAutoJob = useCallback((name) => {
    if (uploadWonRef.current || cancelledRef.current) return;
    // Laufenden Job stilllegen (wir polling-en einfach die alte ID nicht
    // mehr weiter — der Backend-Job läuft still zu Ende, Ergebnis wird
    // verworfen).
    if (activeJobIdRef.current) {
      activeJobIdRef.current = null;
      if (timerRef.current) clearTimeout(timerRef.current);
    }
    startedNameRef.current = name;
    setAutoStatus("pending");
    setAutoProgress("Suche wird gestartet…");
    setAutoResult(null);
    setAutoError("");
    pollCountRef.current = 0;

    // Rekursiver Poll-Loop (eingebettet, damit useCallback ohne Deps auskommt).
    const scheduleNext = (jobId) => {
      if (cancelledRef.current || uploadWonRef.current) return;
      if (pollCountRef.current >= MAX_POLLS) {
        setAutoStatus("error");
        setAutoError("Zeitüberschreitung bei der SDB-Suche.");
        return;
      }
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(async () => {
        if (cancelledRef.current || uploadWonRef.current) return;
        if (activeJobIdRef.current !== jobId) return; // Job wurde stillgelegt
        pollCountRef.current += 1;
        try {
          const resp = await api.getSdbFetchExtractStatus(jobId);
          if (activeJobIdRef.current !== jobId) return;
          if (cancelledRef.current || uploadWonRef.current) return;
          const st = resp.status || "pending";
          setAutoStatus(st);
          if (resp.progress_message) setAutoProgress(resp.progress_message);
          if (resp.result) setAutoResult(resp.result);
          if (resp.error) setAutoError(resp.error);
          if (st !== "done" && st !== "error") scheduleNext(jobId);
        } catch (e) {
          if (cancelledRef.current) return;
          setAutoStatus("error");
          setAutoError(e?.response?.data?.detail || e.message || "Poll-Fehler");
        }
      }, POLL_INTERVAL_MS);
    };

    api.startSdbFetchExtract(name, "").then((resp) => {
      if (cancelledRef.current || uploadWonRef.current) return;
      if (startedNameRef.current !== name) return; // veralteter Job
      activeJobIdRef.current = resp.job_id;
      setAutoJobId(resp.job_id);
      const s = resp.status || "pending";
      setAutoStatus(s);
      scheduleNext(resp.job_id);
    }).catch((e) => {
      if (cancelledRef.current) return;
      setAutoStatus("error");
      setAutoError(e?.response?.data?.detail || e.message || "Startfehler");
    });
  }, []);

  // --- Trigger-Modell: 500ms Debounce nach Produktnamen-Änderung ----------
  useEffect(() => {
    if (!open) return;
    const name = productName.trim();
    // Wenn Upload bereits gewonnen hat, keinen Auto-Job mehr starten.
    if (uploadWonRef.current) return;
    // Wenn derselbe Name schon einen laufenden Job hat, nichts tun.
    if (name && name === startedNameRef.current
        && (autoStatus === "pending" || autoStatus === "searching"
            || autoStatus === "extracting" || autoStatus === "done")) {
      return;
    }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!name || name.length < 2) {
      // Kein plausibler Name → laufenden Job stilllegen
      if (autoStatus !== "idle" && autoStatus !== "done"
          && autoStatus !== "error" && autoStatus !== "cancelled") {
        setAutoStatus("cancelled");
        activeJobIdRef.current = null;
      }
      return;
    }
    debounceRef.current = setTimeout(() => {
      _startAutoJob(name);
    }, PRODUCT_NAME_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [productName, open, autoStatus, _startAutoJob]);

  // --- Manueller Upload (Drag-Drop oder File-Picker) ----------------------
  const handleFile = useCallback(async (file) => {
    if (!file) return;
    if (!productName.trim()) {
      setUploadError("Bitte zuerst den Produktnamen eintragen.");
      setUploadStatus("error");
      return;
    }
    setUploadError("");
    setUploadStatus("uploading");
    setUploadResult(null);
    // User-Upload gewinnt immer — laufenden Auto-Job vorm Anzeigen abschalten.
    uploadWonRef.current = true;
    activeJobIdRef.current = null;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setAutoStatus("cancelled");
    try {
      const resp = await api.uploadSdbManual(file, productName.trim(), "");
      if (cancelledRef.current) return;
      if (resp.sdb_status === "archived_preview") {
        setUploadResult(resp);
        setUploadStatus("done");
      } else {
        setUploadError(
          resp.error
          || "PDF konnte nicht ausgelesen werden. Bitte versuchen Sie ein "
          +  "anderes PDF — z. B. das Original-SDB von der Hersteller-Website.",
        );
        setUploadStatus("error");
      }
    } catch (e) {
      if (cancelledRef.current) return;
      setUploadError(e?.response?.data?.detail || e.message || "Upload-Fehler");
      setUploadStatus("error");
      uploadWonRef.current = false; // Fehlgeschlagen — Auto-Job darf wieder
    }
  }, [productName]);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handleFile(f);
  }, [handleFile]);

  const handlePickFile = useCallback((e) => {
    const f = e.target.files?.[0];
    if (f) handleFile(f);
    e.target.value = ""; // damit derselbe File nochmal ausgewählt werden kann
  }, [handleFile]);

  // --- Vollständigkeits-Check der Pflichtfelder ---------------------------
  const isQuantityCustom = quantityTierIndex === "custom";
  const hazmatComplete = useMemo(() => {
    if (!productName.trim()) return false;
    if (quantityTierIndex === null) return false;
    if (isQuantityCustom && (!quantityCustomAmount.trim() || !quantityCustomUnit.trim())) return false;
    if (!frequencyCode) return false;
    if (substitutionChecked === null) return false;
    if (substitutionChecked === false
        && !substitutionReasonCode && !substitutionFreeText.trim()) return false;
    return true;
  }, [productName, quantityTierIndex, isQuantityCustom, quantityCustomAmount,
      quantityCustomUnit, frequencyCode, substitutionChecked,
      substitutionReasonCode, substitutionFreeText]);

  // --- Effektives SDB-Ergebnis (Upload gewinnt) ---------------------------
  const effectiveResult = uploadResult || autoResult;
  const effectiveSdbStatus = effectiveResult?.sdb_status || null;
  const hasSdbExtract = (uploadStatus === "done" && uploadResult?.sdb_status === "archived_preview")
    || (autoStatus === "done" && autoResult?.sdb_status === "archived_preview" && !uploadWonRef.current);

  // Q++ (24.08.2026, Schritt 3): "Verifizierter Fund" — das Backend hat den
  // Prefetch schon angelegt (mit Identität + PDF), der Extract läuft noch.
  // Trifft im Auto-Zweig ab `status=extracting` mit `result.verifier_ready=true`
  // zu und bleibt im `done+archived_preview`-Zustand ebenfalls wahr. Der
  // Upload-Zweig ist synchron und daher hier nicht relevant — für ihn gilt
  // weiterhin `hasSdbExtract`. `prefetch_id` MUSS vorhanden sein, weil
  // `generate` es sonst nicht laden kann.
  const hasVerifiedHit = (!uploadWonRef.current)
    && (autoStatus === "extracting" || autoStatus === "done")
    && autoResult?.verifier_ready === true
    && !!autoResult?.prefetch_id;

  // Extract im Auto-Zweig endgültig fehlgeschlagen? (parse_error / llm_error /
  // unreadable — nach `done`). Wird für den Abbruch der Warte-Sequenz genutzt.
  const autoExtractFailed = autoStatus === "done"
    && autoResult?.verifier_ready === true
    && autoResult?.sdb_status !== "archived_preview"
    && autoResult?.sdb_status !== "not_found";

  const searchStateLabel = useMemo(() => {
    if (uploadStatus === "uploading") return "PDF wird ausgelesen…";
    if (uploadStatus === "done") return null;
    if (uploadStatus === "error") return null;
    if (autoStatus === "idle") return "Suche startet automatisch, sobald ein Produktname vorliegt.";
    if (autoStatus === "pending" || autoStatus === "searching"
        || autoStatus === "extracting") return autoProgress || "SDB-Suche läuft…";
    if (autoStatus === "cancelled") return "Suche pausiert (Produktname geändert oder Datei hochgeladen).";
    if (autoStatus === "done") {
      if (autoResult?.sdb_status === "archived_preview") return null;
      if (autoResult?.sdb_status === "not_found")
        return "Kein SDB im Netz gefunden — bitte laden Sie das SDB als PDF "
          + "selbst hoch (erhältlich beim Hersteller oder Händler).";
      return "SDB-Verarbeitung abgeschlossen (mit Einschränkungen).";
    }
    if (autoStatus === "error") return autoError || "Suchfehler.";
    return "";
  }, [autoStatus, autoResult, autoError, autoProgress, uploadStatus]);

  // --- Submit-Bau der Declaration -----------------------------------------
  const buildDeclaration = useCallback(() => {
    const qq = initial?.quantityQuestion || {};
    const customLabel = `${quantityCustomAmount.trim()} ${quantityCustomUnit.trim()}`.trim();
    return {
      product_name: productName.trim(),
      product_manufacturer: null,
      product_article_number: null,
      quantity_unit: isQuantityCustom ? "generisch" : (qq.unit || "generisch"),
      quantity_tier_index: isQuantityCustom ? 1 : quantityTierIndex,
      quantity_tier_label: isQuantityCustom
        ? customLabel
        : (qq.tier_labels || [])[quantityTierIndex] || "",
      frequency: frequencyCode,
      substitution_checked: !!substitutionChecked,
      substitution_reason_code:
        substitutionChecked === false && substitutionReasonCode
          ? substitutionReasonCode
          : null,
      substitution_free_text:
        substitutionFreeText.trim() ? substitutionFreeText.trim() : null,
    };
  }, [initial, productName, quantityTierIndex, isQuantityCustom,
      quantityCustomAmount, quantityCustomUnit, frequencyCode,
      substitutionChecked, substitutionReasonCode, substitutionFreeText]);

  const handleConfirmWithSdb = useCallback(() => {
    if (!hazmatComplete) return;
    // Extract bereits fertig (Upload-Zweig oder Auto-done+archived_preview):
    // direkt auslösen — identisch zum bisherigen Verhalten.
    if (hasSdbExtract) {
      if (generateTriggeredRef.current) return;
      generateTriggeredRef.current = true;
      const decl = buildDeclaration();
      const prefetchId = effectiveResult?.prefetch_id || null;
      onConfirm(decl, prefetchId);
      return;
    }
    // Q++ Schritt 3 (24.08.2026): Verifier ist durch, aber der Extract läuft
    // noch. Wir gehen in den sichtbaren Warte-Zustand — der `useEffect` unten
    // beobachtet das Polling und ruft `handleConfirmWithSdb` erneut auf,
    // sobald `hasSdbExtract` true wird. Bei Extract-Fehler bricht der
    // Watcher den Warte-Zustand ab, ohne `generate` zu triggern.
    if (hasVerifiedHit && !uploadWonRef.current) {
      setAwaitingExtract(true);
    }
  }, [hazmatComplete, hasSdbExtract, hasVerifiedHit, buildDeclaration,
      effectiveResult, onConfirm]);

  // --- Q++ Schritt 3 · Auto-Trigger nach fertigem Extract -----------------
  // Wenn der User geklickt hat, während der Extract noch lief, und der
  // Backend-Extract jetzt fertig ist (oder final gescheitert ist), ziehen
  // wir das Confirm einmal automatisch nach — bzw. beenden das Warten bei
  // Fehler geräuschlos (der bestehende Banner zeigt den Grund).
  useEffect(() => {
    if (!awaitingExtract) return;
    if (cancelledRef.current) { setAwaitingExtract(false); return; }
    if (hasSdbExtract) {
      setAwaitingExtract(false);
      if (generateTriggeredRef.current) return;
      generateTriggeredRef.current = true;
      const decl = buildDeclaration();
      const prefetchId = effectiveResult?.prefetch_id || null;
      onConfirm(decl, prefetchId);
      return;
    }
    if (autoExtractFailed || autoStatus === "error") {
      setAwaitingExtract(false);
    }
  }, [awaitingExtract, hasSdbExtract, autoExtractFailed, autoStatus,
      buildDeclaration, effectiveResult, onConfirm]);

  const quantityQuestion = initial?.quantityQuestion || {};
  const tierLabels = quantityQuestion.tier_labels || [];

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent
        data-testid="hazmat-sdb-sighting-modal"
        className="max-w-lg lg:max-w-4xl max-h-[92vh] overflow-y-auto overflow-x-hidden gap-0 p-0"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-slate-100">
          <DialogTitle
            data-testid="hazmat-sdb-sighting-title"
            className="flex items-center gap-2.5 text-lg font-semibold text-slate-900"
          >
            <span className="flex items-center justify-center w-8 h-8 rounded-full shrink-0 bg-amber-500 text-white">
              <ShieldAlert className="w-4 h-4" />
            </span>
            Angaben zum Gefahrstoff
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-600 leading-relaxed pt-1">
            Bitte drei kurze Pflichtangaben. Parallel suchen wir automatisch
            das aktuelle Sicherheitsdatenblatt — oder Sie ziehen es rechts
            direkt hinein, wenn Sie es schon haben.
          </DialogDescription>
        </DialogHeader>

        {/* Grid mit zwei Spalten ab lg (≥ 1024 px). Auf iPad-Hochformat und
            schmalen Notebooks liegen Pflichtangaben und SDB-Bereich
            untereinander — für Touch-Bedienung natürlicher. Auf breiteren
            Fenstern erscheint die klassische Zwei-Spalten-Ansicht. Rechte
            Spalte trägt zusätzlich `min-w-0`, damit ihr Inhalt (SDB-Extract-
            Preview mit dl-Grid, s. u.) den Track NICHT über die Modal-Breite
            hinaus dehnen kann.  Sprint Q++ · Modal-Overflow-Fix (25.08.2026). */}
        <div className="grid lg:grid-cols-[1fr,320px] gap-0">
          {/* Linke Spalte: Pflichtangaben --------------------------------- */}
          <div className="px-6 py-4 space-y-5 border-b lg:border-b-0 lg:border-r border-slate-100 min-w-0">
            {/* Produkt-Name */}
            <div>
              <label className="block text-[15px] font-semibold text-slate-900">
                Produkt-Name
              </label>
              <p className="text-xs text-slate-500 mt-1">Wie heißt das Produkt (für das Verzeichnis)?</p>
              <input
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                data-testid="hazmat-product-name"
                placeholder="z. B. Salzsäure 32 %"
                className="mt-2 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus-visible:ring-2 focus-visible:ring-slate-900 focus-visible:outline-none"
                maxLength={200}
              />
            </div>

            {/* Menge */}
            <div>
              <label className="block text-[15px] font-semibold text-slate-900">
                Lagermenge
              </label>
              <p className="text-xs text-slate-500 mt-1">Wie viel davon habt ihr ungefähr im Betrieb?</p>
              <div className="mt-2 space-y-2" data-testid="hazmat-quantity-tiers">
                {tierLabels.map((label, idx) => (
                  <label
                    key={`q-${idx}`}
                    className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition ${quantityTierIndex === idx ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <input
                      type="radio"
                      name="hazmat-quantity"
                      checked={quantityTierIndex === idx}
                      onChange={() => setQuantityTierIndex(idx)}
                      data-testid={`hazmat-quantity-tier-${idx}`}
                      className="w-4 h-4 accent-slate-900"
                    />
                    <span className="text-sm text-slate-800">{label}</span>
                  </label>
                ))}
                <label
                  className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer transition ${isQuantityCustom ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}
                >
                  <input
                    type="radio"
                    name="hazmat-quantity"
                    checked={isQuantityCustom}
                    onChange={() => setQuantityTierIndex("custom")}
                    data-testid="hazmat-quantity-tier-custom"
                    className="mt-0.5 w-4 h-4 accent-slate-900"
                  />
                  <div className="flex-1 min-w-0">
                    <span className="text-sm text-slate-800">Andere Menge angeben</span>
                    {isQuantityCustom && (
                      <div className="mt-2 flex gap-2 flex-wrap">
                        <input
                          type="text"
                          value={quantityCustomAmount}
                          onChange={(e) => setQuantityCustomAmount(e.target.value)}
                          placeholder="Menge (z. B. 6 ml)"
                          data-testid="hazmat-quantity-custom-amount"
                          className="flex-1 min-w-0 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm outline-none"
                          maxLength={40}
                        />
                        <input
                          type="text"
                          value={quantityCustomUnit}
                          onChange={(e) => setQuantityCustomUnit(e.target.value)}
                          placeholder="Einheit (z. B. Flasche)"
                          data-testid="hazmat-quantity-custom-unit"
                          className="flex-1 min-w-0 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm outline-none"
                          maxLength={40}
                        />
                      </div>
                    )}
                  </div>
                </label>
              </div>
            </div>

            {/* Häufigkeit */}
            <div>
              <label className="block text-[15px] font-semibold text-slate-900">
                Einsatzhäufigkeit
              </label>
              <p className="text-xs text-slate-500 mt-1">Wie oft setzt ihr das ein?</p>
              <div className="mt-2 space-y-2" data-testid="hazmat-frequency-tiers">
                {FREQUENCY_TIERS.map((t) => (
                  <label
                    key={t.code}
                    className={`flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition ${frequencyCode === t.code ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-300"}`}
                  >
                    <input
                      type="radio"
                      name="hazmat-frequency"
                      checked={frequencyCode === t.code}
                      onChange={() => setFrequencyCode(t.code)}
                      data-testid={`hazmat-frequency-${t.code}`}
                      className="w-4 h-4 accent-slate-900"
                    />
                    <span className="text-sm text-slate-800">{t.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Substitution */}
            <div>
              <label className="block text-[15px] font-semibold text-slate-900">
                Substitutionsprüfung
              </label>
              <p className="text-xs text-slate-500 mt-1">
                GefStoffV verlangt, dass Sie prüfen, ob dieser Gefahrstoff durch einen weniger gefährlichen Stoff ersetzt werden kann.
              </p>
              <div className="mt-2 space-y-2">
                <div className="pl-6" data-testid="hazmat-substitution-nocheck-block">
                    <p className="text-xs text-slate-500 mb-2">Sie haben geprüft und keinen Ersatz gefunden? Bitte wählen Sie das Prüfungsergebnis:</p>
                    <div className="space-y-2">
                      {SUBSTITUTION_REASONS.map((r) => (
                        <label
                          key={r.code}
                          className={`flex items-start gap-3 rounded-md border px-3 py-2 cursor-pointer transition ${substitutionReasonCode === r.code ? "border-amber-500 bg-amber-50/40" : "border-slate-200 hover:border-slate-300"}`}
                        >
                          <input
                            type="radio"
                            name="hazmat-substitution-reason"
                            checked={substitutionReasonCode === r.code}
                            onChange={() => {
                              setSubstitutionChecked(false);
                              setSubstitutionReasonCode(r.code);
                            }}
                            data-testid={`hazmat-substitution-reason-${r.code}`}
                            className="mt-1 w-4 h-4 accent-amber-600"
                          />
                          <span className="text-sm text-slate-800">{r.label}</span>
                        </label>
                      ))}
                    </div>
                    <div className="mt-2">
                      <label className="text-xs text-slate-500">Zusätzlicher Hinweis (optional):</label>
                      <Textarea
                        value={substitutionFreeText}
                        onChange={(e) => {
                          setSubstitutionFreeText(e.target.value);
                          if (e.target.value.trim() && substitutionChecked === null) {
                            setSubstitutionChecked(false);
                          }
                        }}
                        rows={2}
                        placeholder={`Freitext, z. B. „Umstellung auf 2K-Klebstoff geplant Q4"`}
                        data-testid="hazmat-substitution-freetext"
                        className="mt-1 min-h-[60px] bg-white border-slate-300 px-3 py-2 text-sm resize-none"
                      />
                    </div>
                  </div>
              </div>
            </div>
          </div>

          {/* Rechte Spalte: SDB-Bereich (Auto-Suche + Drop-Zone) ------------ */}
          {/* Rechte Spalte: SDB-Bereich (Suche, Upload, Extract-Preview) --
              `min-w-0` ist essenziell, damit das innere `dl`-Grid unten
              (Zeile ~836 mit `grid-cols-[minmax(0,max-content)_1fr]`) den
              Track nicht über die Spaltenbreite hinaus aufblähen kann.
              Ohne dies bricht die 320-px-Spalte auf, sobald der Extract
              lange Werte enthält (Hersteller-URL, GHS-Codes, lange PSA). */}
          <div className="px-6 py-4 space-y-4 bg-slate-50/40 min-w-0">
            <div>
              <div className="text-[15px] font-semibold text-slate-900">Sicherheitsdatenblatt</div>
              <p className="text-xs text-slate-500 mt-1">
                Wir suchen automatisch — oder Sie ziehen Ihre eigene PDF hierher.
              </p>
            </div>

            {/* Drop-Zone (immer sichtbar) */}
            <label
              onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
              onDrop={handleDrop}
              data-testid="hazmat-sdb-dropzone"
              className={`flex flex-col items-center justify-center gap-1 rounded-md border-2 border-dashed cursor-pointer px-3 py-6 text-center transition ${
                dragActive
                  ? "border-emerald-500 bg-emerald-50"
                  : uploadStatus === "done"
                  ? "border-emerald-400 bg-emerald-50"
                  : "border-slate-300 bg-white hover:border-slate-400"
              }`}
            >
              {uploadStatus === "done" ? (
                <>
                  <FileText className="w-5 h-5 text-emerald-600" />
                  <div className="text-sm font-medium text-emerald-800">SDB hochgeladen ✓</div>
                  <div className="text-xs text-slate-500">Klicken, um zu ersetzen.</div>
                </>
              ) : uploadStatus === "uploading" ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin text-slate-500" />
                  <div className="text-sm text-slate-700">PDF wird ausgelesen…</div>
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5 text-slate-500" />
                  <div className="text-sm font-medium text-slate-800">
                    PDF hier hineinziehen
                  </div>
                  <div className="text-xs text-slate-500">oder klicken, um Datei auszuwählen</div>
                </>
              )}
              <input
                type="file"
                accept="application/pdf,.pdf"
                onChange={handlePickFile}
                className="hidden"
                data-testid="hazmat-sdb-file-input"
              />
            </label>

            {uploadError && (
              <div
                data-testid="hazmat-sdb-upload-error"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900"
              >
                {uploadError}
              </div>
            )}

            {/* Auto-Such-Status */}
            {!uploadWonRef.current && (
              <div
                data-testid="hazmat-sdb-sighting-banner"
                aria-live="polite"
                className={`flex items-start gap-2 rounded-md border px-3 py-2 text-xs ${
                  autoStatus === "error"
                    ? "border-red-200 bg-red-50 text-red-900"
                    : autoStatus === "done" && autoResult?.sdb_status === "archived_preview"
                    ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                    : autoStatus === "done"
                    ? "border-amber-200 bg-amber-50 text-amber-900"
                    : "border-slate-200 bg-white text-slate-700"
                }`}
              >
                {autoStatus === "pending" || autoStatus === "searching" || autoStatus === "extracting" ? (
                  <Loader2 className="w-3.5 h-3.5 mt-0.5 animate-spin shrink-0" />
                ) : autoStatus === "done" && autoResult?.sdb_status === "archived_preview" ? (
                  <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                ) : (
                  <ShieldAlert className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                )}
                <span>{searchStateLabel}</span>
              </div>
            )}

            {/* Q++ Schritt 3 (24.08.2026): Sekundär-Hinweis, sobald der
                Verifier durch ist, aber der Extract noch läuft. Der
                Confirm-Button ist bereits klickbar. */}
            {!uploadWonRef.current && autoStatus === "extracting"
             && autoResult?.verifier_ready === true && (
              <div
                data-testid="hazmat-sdb-sighting-verified-badge"
                className="flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900"
                aria-live="polite"
              >
                <ShieldCheck className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  SDB gefunden — Details werden noch ausgelesen. Sie können
                  bereits jetzt &bdquo;Werte übernehmen &amp; Welt erstellen&ldquo; klicken.
                </span>
              </div>
            )}

            {/* Fund-Anzeige (Extract-Preview) */}
            {hasSdbExtract && effectiveResult && (
              <div className="space-y-2" data-testid="hazmat-sdb-sighting-preview">
                {effectiveResult.source_url && (
                  <div className="text-xs text-slate-500 flex items-center gap-1 flex-wrap min-w-0">
                    Quelle:
                    <a
                      href={effectiveResult.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline inline-flex items-center gap-1 break-all min-w-0"
                      data-testid="hazmat-sdb-sighting-source-link"
                    >
                      {(() => {
                        try { return new URL(effectiveResult.source_url).hostname; }
                        catch { return effectiveResult.source_url; }
                      })()}
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                  </div>
                )}
                {effectiveResult.identity_reasoning && (
                  <div
                    data-testid="hazmat-sdb-sighting-identity-reasoning"
                    className="rounded-md border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] text-slate-700"
                  >
                    <div className="font-medium text-slate-800 mb-0.5">
                      {effectiveResult.identity_decided_by === "user_upload"
                        ? "Eigenverantwortlicher Upload"
                        : "KI-Plausibilitätsprüfung"}
                    </div>
                    <div>{effectiveResult.identity_reasoning}</div>
                  </div>
                )}
                {DISPLAY_SECTIONS.map((section) => {
                  const secData = effectiveResult.extract?.[section.key];
                  const nonEmpty = section.fields.filter(([k]) => _val(secData, k));
                  if (nonEmpty.length === 0) return null;
                  return (
                    <div
                      key={section.key}
                      data-testid={`hazmat-sdb-section-${section.key}`}
                      className="border rounded-md p-2 bg-white text-xs"
                    >
                      <div className="font-semibold text-slate-800 mb-1">{section.title}</div>
                      {/* Sprint Q++ · Modal-Overflow-Fix (25.08.2026):
                          `minmax(0,max-content)` statt reinem `max-content`
                          + `min-w-0` + `break-words` schützen die
                          Extract-Preview vor Track-Overflow. Lange Werte
                          (URL, GHS-Codes, PSA-Text) umbrechen jetzt sauber
                          innerhalb der Spalte, statt sie zu sprengen —
                          gilt AUCH bei 375-px-Handy-Breite (viewport-
                          unabhängig, kein `md:`/`lg:`-Gate). */}
                      <dl className="grid grid-cols-[minmax(0,max-content)_minmax(0,1fr)] gap-x-3 gap-y-0.5 min-w-0">
                        {nonEmpty.map(([k, label]) => (
                          <div key={k} className="contents">
                            <dt className="text-slate-500 break-words">{label}:</dt>
                            <dd className="text-slate-900 min-w-0 break-words">{_val(secData, k)}</dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 sm:justify-between px-6 py-4 border-t border-slate-100">
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={onCancel}
              data-testid="hazmat-sdb-sighting-cancel-btn"
            >
              <X className="w-4 h-4 mr-2" /> Abbrechen
            </Button>
            {onFleeHazmat && (
              <Button
                type="button"
                variant="ghost"
                onClick={onFleeHazmat}
                data-testid="hazmat-sdb-sighting-flee-btn"
                className="text-slate-500 hover:text-slate-800"
              >
                Kein Gefahrstoff — normale Welt erstellen
              </Button>
            )}
          </div>
          <div className="flex flex-col sm:flex-row gap-2">
            <Button
              type="button"
              disabled={!hazmatComplete || (!hasSdbExtract && !hasVerifiedHit) || awaitingExtract}
              onClick={handleConfirmWithSdb}
              data-testid="hazmat-sdb-sighting-confirm-btn"
            >
              {awaitingExtract ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  SDB wird noch fertig ausgelesen — einen Moment…
                </>
              ) : (
                <>
                  <Check className="w-4 h-4 mr-2" />
                  Werte übernehmen &amp; Welt erstellen
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
